import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, DataTable, Group, Icon, Text } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import { ActionButtons, Filters, TagsWithPlus } from "components/custom";
import { IMPORTED_QUESTIONS_VIEW } from "constants/app-paths";
import { useGetImportedQuestionsQuery } from "features/importedQuestions/apis";
import { useGetSpecialitiesQuery } from "features/script/api";

const DEFAULT_PAGE_SIZE = 30;

const buildQueryParams = (queryObj, cursorByPage, currentPage) => {
  const limit = queryObj?.per_page
    ? Number(queryObj.per_page)
    : DEFAULT_PAGE_SIZE;
  const params = { limit };
  const cursor = cursorByPage?.[currentPage] || null;
  if (cursor) {
    params.cursor = cursor;
  }
  if (queryObj?.search) {
    params.search = queryObj.search;
  }
  if (queryObj?.specialityId) {
    params.specialityId = queryObj.specialityId;
  }
  return params;
};

const getPlainText = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const CollapsibleText = ({ text, collapsibleLength = 100 }) => {
  const [seeMore, setSeeMore] = useState(false);
  const showToggle = text?.length > collapsibleLength;
  const content =
    !seeMore && showToggle ? `${text?.substring(0, collapsibleLength)}...` : text;

  return (
    <div>
      <Text style={{ whiteSpace: "break-spaces" }}>{content}</Text>
      {showToggle && (
        <Button
          type="link"
          size="small"
          className="pointer text-underline p-0"
          style={{ height: "auto" }}
          onClick={() => setSeeMore((prev) => !prev)}
          text={seeMore ? "See Less" : "See More"}
        />
      )}
    </div>
  );
};

const ImportedQuestionsListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryObj = Object.fromEntries([...searchParams]);
  const [cursorByPage, setCursorByPage] = useState({ 1: null });
  const [currentPage, setCurrentPage] = useState(
    queryObj?.page ? Number(queryObj.page) : 1
  );
  const pageSize = queryObj?.per_page
    ? Number(queryObj.per_page)
    : DEFAULT_PAGE_SIZE;
  const filterKey = JSON.stringify(
    Object.fromEntries(
      Object.entries(queryObj).filter(
        ([key]) => key !== "page" && key !== "per_page"
      )
    )
  );

  const {
    data: importedQuestions,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetImportedQuestionsQuery(
    buildQueryParams(queryObj, cursorByPage, currentPage),
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    setCurrentPage(queryObj?.page ? Number(queryObj.page) : 1);
  }, [queryObj?.page]);

  useEffect(() => {
    setCursorByPage({ 1: null });
    if (queryObj?.page && Number(queryObj.page) !== 1) {
      setSearchParams({ ...queryObj, page: 1 });
    }
  }, [filterKey]);

  useEffect(() => {
    if (importedQuestions?.nextCursor) {
      setCursorByPage((prev) => ({
        ...prev,
        [currentPage + 1]: importedQuestions.nextCursor,
      }));
    }
  }, [importedQuestions?.nextCursor, currentPage]);

  const { data: specialties } = useGetSpecialitiesQuery();

  const columns = [
    {
      title: "No.",
      dataIndex: "questionNumber",
      className: "tableStartSpace",
      width: 100,
    },
    {
      title: "Question",
      dataIndex: "description",
    },
    {
      title: "Speciality",
      dataIndex: "speciality",
    },
    { title: "Secondary Speciality", dataIndex: "secondarySpeciality" },
    {
      title: "Topics",
      dataIndex: "topics",
    },
    {
      title: "Actions",
      dataIndex: "actions",
    },
  ];

  const getActionsList = (question) => [
    {
      key: "view_question",
      icon: <Icon icon="IoMdEye" />,
      content: "View Question",
      title: "View Question",
      className: "pointer",
      onClick: () => {
        navigate(IMPORTED_QUESTIONS_VIEW.replace(":id", question?._id));
      },
    },
  ];

  const tableData = useCallback(() => {
    return importedQuestions?.data?.length
      ? importedQuestions.data.map((question, index) => ({
        ...question,
        key: index + 1,
        questionNumber: index + 1,
        description: (
          <CollapsibleText
            text={getPlainText(question?.description || "")}
            collapsibleLength={100}
          />
        ),
        speciality: (
          <TagsWithPlus
            data={
              question?.speciality?.length
                ? question.speciality
                  .map((item) => item?.name)
                  .filter(Boolean)
                : ["-"]
            }
            plusAfter={3}
          />
        ),
        secondarySpeciality: (
          <TagsWithPlus
            data={question?.secondarySpeciality?.length ? question.secondarySpeciality.map((item) => item?.name) : ["-"]}
            plusAfter={3}
          />
        ),
        topics: (
          <TagsWithPlus
            data={question?.topics?.length ? question.topics : ["-"]}
            plusAfter={3}
          />
        ),
        actions: <ActionButtons list={getActionsList(question)} />,
      }))
      : [];
  }, [importedQuestions]);

  const filterList = useMemo(
    () => [
      {
        label: "none",
        type: "input",
        key: "search",
        placeholder: "Search by Keyword",
      },
      {
        label: "none",
        list: specialties?.data?.length
          ? specialties.data.map((spec) => ({
            label: spec.topic,
            value: spec._id,
          }))
          : [],
        type: "select",
        key: "specialityId",
        placeholder: "Select Specialty",
      },
    ],
    [specialties?.data]
  );

  return (
    <PageLayout showSubMenu={true}>
      <Group
        gap="16px"
        className="listings-content-adjustments"
        style={{ marginTop: "-20px" }}
      >
        <Filters list={filterList} />
        <DataTable
          pagination={{
            totalCount: (currentPage + (importedQuestions?.nextCursor ? 1 : 0)) *
              pageSize,
            pageOptions: [DEFAULT_PAGE_SIZE, 60, 90],
          }}
          stickyOffset={0}
          paginationOnBottom={true}
          onChangePage={(params) => setSearchParams(params)}
          columns={columns}
          data={tableData()}
          loading={isLoading}
          error={error}
          onErrorRetry={refetch}
          skeletonLoading={isFetching}
        />
      </Group>
    </PageLayout>
  );
};

export default ImportedQuestionsListing;
